/**
 * PDF Orbit Menu
 *
 * Full port of orbit-summer-2022-demo/src/components/OrbitMenu.tsx
 * with DueCount badge, prompt visibility control, and menu items.
 *
 * Demo source references:
 * - OrbitMenu.tsx lines 20-95: OrbitMenuButton
 * - OrbitMenu.tsx lines 97-125: OrbitMenuBackground
 * - OrbitMenu.tsx lines 127-141: OrbitMenuLogo
 * - OrbitMenu.tsx lines 169-359: Main OrbitMenu
 * - OrbitMenu.tsx lines 361-404: DueCount
 * - OrbitMenuPromptVisibilityControl.tsx: None/Saved/All segmented control
 * - MenuItem.tsx: Menu items with titles/shortcuts
 * - Type.tsx: Label, LabelSmall typography
 * - ShortcutKey.tsx: Keyboard shortcut indicator
 */

import React, { useCallback, useEffect, useRef, useState, ReactNode } from "react";

// Static paths to images in public/images/ folder
const Starburst = "/images/Starburst-48.png";
const XIcon = "/images/X.png";
const Logo = "/images/Logo.png";

// ============================================================================
// Types and Enums
// ============================================================================

export enum PromptVisibilitySetting {
  None = "None",
  All = "All",
}

export enum LabelColor {
  AccentPrimary = "AccentPrimary",
  FGPrimary = "FGPrimary",
  FGSecondaryLarge = "FGSecondaryLarge",
  FGSecondarySmall = "FGSecondarySmall",
  FGDisabled = "FGDisabled",
  White = "White",
}

const colorsByEnumValue: Record<LabelColor, string> = {
  [LabelColor.AccentPrimary]: "var(--accentPrimary)",
  [LabelColor.FGPrimary]: "var(--fgPrimary)",
  [LabelColor.FGSecondaryLarge]: "var(--fgSecondaryLarge)",
  [LabelColor.FGSecondarySmall]: "var(--fgSecondarySmall)",
  [LabelColor.FGDisabled]: "var(--fgDisabled)",
  [LabelColor.White]: "white",
};

// ============================================================================
// Typography Components (from Type.tsx)
// ============================================================================

interface TypeProps {
  text: string;
  color?: LabelColor;
}

// Label - 17px Dr-ExtraBold (Type.tsx lines 33-53)
function Label({ text, color = LabelColor.FGPrimary }: TypeProps) {
  return (
    <div
      style={{
        fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
        fontSize: 17,
        lineHeight: "12px",
        letterSpacing: "0.01em",
        marginTop: -1,
        paddingBottom: 1,
        color: colorsByEnumValue[color],
      }}
    >
      {text}
    </div>
  );
}

// LabelSmall - 15px Dr-ExtraBold (Type.tsx lines 55-66)
function LabelSmall({ text, color = LabelColor.FGPrimary }: TypeProps) {
  return (
    <div
      style={{
        fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
        fontSize: 15,
        lineHeight: "12px",
        letterSpacing: "0.02em",
        color: colorsByEnumValue[color],
      }}
    >
      {text}
    </div>
  );
}

// labelSmallStyle for DueCount (Type.tsx lines 55-60)
const labelSmallStyle: React.CSSProperties = {
  fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
  fontSize: 15,
  lineHeight: "12px",
  letterSpacing: "0.02em",
};

// ============================================================================
// ShortcutKey Component (from ShortcutKey.tsx)
// ============================================================================

interface ShortcutKeyProps {
  shortcutKey: string;
  hover: boolean;
}

function ShortcutKey({ shortcutKey, hover }: ShortcutKeyProps) {
  return (
    <div
      style={{
        position: "relative",
        width: 20,
        height: 20,
        background: "var(--bgSecondary)",
        borderRadius: 2,
        border: hover ? "2px solid var(--fgTertiary)" : "none",
      }}
    >
      <span
        style={{
          position: "absolute",
          width: 12,
          height: 16,
          left: "calc(50% - 6px)",
          top: "calc(50% - 9px)",
          fontFamily: "Dr-ExtraBold, system-ui, sans-serif",
          fontSize: 15,
          lineHeight: "17px",
          textAlign: "center",
          letterSpacing: "0.03em",
          color: "var(--fgSecondarySmall)",
        }}
      >
        {shortcutKey}
      </span>
    </div>
  );
}

// ============================================================================
// hoverAndActiveStyles helper (from hoverAndActiveStyles.tsx)
// ============================================================================

const hoverActiveButtonStyle = `
  /* Base hover styles for menu items (from hoverAndActiveStyles.tsx) */
  .orbit-menu-hover-button {
    display: block;
    position: relative;
  }
  .orbit-menu-hover-button::before {
    position: absolute;
    content: "";
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    background-color: transparent;
  }
  .orbit-menu-hover-button:hover::before {
    background-color: var(--hoverLayer);
  }
  .orbit-menu-hover-button:active::before {
    background-color: var(--pressedLayer);
  }

  /* Starburst button with circular hover (matching Andy's OrbitMenu.tsx lines 30-51) */
  .orbit-starburst-button {
    display: block;
    position: relative;
  }
  .orbit-starburst-button::before {
    position: absolute;
    content: "";
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    pointer-events: none;
    background-color: transparent;
    transition: border-radius 1s var(--expoTiming);
  }
  .orbit-starburst-button:hover::before {
    background-color: var(--hoverLayer);
  }
  .orbit-starburst-button:active::before {
    background-color: var(--pressedLayer);
  }

  /* When menu is open, button hover has corner radius (matching Andy's) */
  .orbit-button-open::before {
    border-radius: 0 0 50% 0;
    transition: border-radius 1s var(--expoTiming);
  }
`;

// ============================================================================
// DueCount Component (from OrbitMenu.tsx lines 361-404)
// ============================================================================

interface DueCountProps {
  count: number;
  menuIsOpen: boolean;
  context: "orbitButton" | "menuItem";
}

function DueCount({ count, menuIsOpen, context }: DueCountProps) {
  return (
    <div
      style={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        paddingBottom: 3,
        backgroundColor: "var(--accentPrimary)",
        filter:
          context === "orbitButton"
            ? "drop-shadow(0px 3px 5px rgba(0, 0, 0, 0.15)) drop-shadow(0px 1px 3px rgba(77, 51, 8, 0.4))"
            : undefined,
        opacity: context === "orbitButton" ? (menuIsOpen ? 0 : 1) : 1,
        transition: context === "orbitButton" ? "var(--fadeTransition)" : undefined,
        transitionDuration: "83ms",
      }}
    >
      <div
        style={{
          ...labelSmallStyle,
          color: "white",
          textAlign: "center",
          flexGrow: 1,
        }}
      >
        {count}
      </div>
    </div>
  );
}

// ============================================================================
// OrbitMenuButton (from OrbitMenu.tsx lines 20-95)
// ============================================================================

interface OrbitMenuButtonProps {
  onClick: () => void;
  menuIsOpen: boolean;
}

function OrbitMenuButton({ onClick, menuIsOpen }: OrbitMenuButtonProps) {
  // Use starburst-button class when closed (circular hover), menu-hover-button when open
  return (
    <button
      onClick={onClick}
      className={menuIsOpen ? "orbit-menu-hover-button orbit-button-open" : "orbit-starburst-button"}
      style={{
        position: "absolute",
        bottom: 0,
        right: 0,
        width: 48,
        height: 48,
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
        pointerEvents: "all",
        // Dodge the bottom chrome border when the menu is open
        clipPath: menuIsOpen ? "inset(3px 0 0 0)" : undefined,
      }}
    >
      {/* Starburst icon */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 48,
          height: 48,
          backgroundColor: "var(--accentPrimary)",
          maskPosition: "center",
          maskRepeat: "no-repeat",
          maskImage: `url(${Starburst})`,
          maskSize: "48px 48px",
          WebkitMaskPosition: "center",
          WebkitMaskRepeat: "no-repeat",
          WebkitMaskImage: `url(${Starburst})`,
          WebkitMaskSize: "48px 48px",
          transition: "var(--fadeTransition)",
          opacity: menuIsOpen ? 0 : 1,
        }}
      />

      {/* Close (X) icon */}
      <div
        style={{
          position: "absolute",
          top: 12,
          left: 12,
          width: 24,
          height: 24,
          backgroundColor: "var(--accentPrimary)",
          maskPosition: "center",
          maskRepeat: "no-repeat",
          maskImage: `url(${XIcon})`,
          maskSize: "24px 24px",
          WebkitMaskPosition: "center",
          WebkitMaskRepeat: "no-repeat",
          WebkitMaskImage: `url(${XIcon})`,
          WebkitMaskSize: "24px 24px",
          transition: "var(--fadeTransition)",
          opacity: menuIsOpen ? 1 : 0,
        }}
      />
    </button>
  );
}

// ============================================================================
// OrbitMenuBackground (from OrbitMenu.tsx lines 97-125)
// ============================================================================

interface OrbitMenuBackgroundProps {
  menuIsOpen: boolean;
  contentsSize: [number, number];
}

function OrbitMenuBackground({ menuIsOpen, contentsSize }: OrbitMenuBackgroundProps) {
  // Andy's original: always render, animate between circular (closed) and rectangular (open)
  return (
    <div
      style={{
        position: "absolute",
        // Position as an "outer" border
        bottom: -3,
        right: -3,
        width: (menuIsOpen ? contentsSize[0] : 48) + 3 * 2,
        height: (menuIsOpen ? contentsSize[1] : 48) + 3 * 2,
        backgroundColor: "var(--bgSecondary)",
        borderColor: "var(--fgTertiary)",
        borderWidth: 3,
        borderRadius: menuIsOpen ? "0 0 27px 0" : 27, // 27 = 48 (width) / 2 + 3 (border)
        borderStyle: "solid",
        boxShadow:
          "0px 7px 20px 6px rgba(77, 51, 8, 0.09), 0px 4px 8px 3px rgba(77, 51, 8, 0.11), 0px 1px 3px rgba(77, 51, 8, 0.1)",
        transition: "width 0.3s var(--expoTiming), height 0.3s var(--expoTiming), border-radius 0.3s var(--expoTiming)",
      }}
    />
  );
}

// ============================================================================
// OrbitMenuLogo (from OrbitMenu.tsx lines 127-141)
// ============================================================================

function OrbitMenuLogo() {
  return (
    <div
      style={{
        backgroundColor: "var(--fgSecondaryLarge)",
        maskPosition: "center",
        maskRepeat: "no-repeat",
        maskImage: `url(${Logo})`,
        maskSize: "61px 32px",
        WebkitMaskPosition: "center",
        WebkitMaskRepeat: "no-repeat",
        WebkitMaskImage: `url(${Logo})`,
        WebkitMaskSize: "61px 32px",
        width: 61,
        height: 32,
      }}
    />
  );
}

// ============================================================================
// MenuItem (from MenuItem.tsx)
// ============================================================================

interface MenuItemProps {
  title: string;
  subtitle?: string;
  shortcutKey?: string;
  onClick: () => void;
  disabled?: boolean;
  isEnabled?: boolean;
}

function MenuItem({
  title,
  subtitle,
  shortcutKey,
  onClick,
  disabled = false,
  isEnabled = true,
}: MenuItemProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Keyboard shortcut handler (MenuItem.tsx lines 34-44)
  useEffect(() => {
    if (!shortcutKey || !isEnabled) return;

    const key = shortcutKey.toLowerCase();
    function onShortcutKey(e: KeyboardEvent) {
      if (e.key === key) {
        e.preventDefault();
        onClick();
      }
    }

    document.addEventListener("keydown", onShortcutKey);
    return () => document.removeEventListener("keydown", onShortcutKey);
  }, [shortcutKey, isEnabled, onClick]);

  return (
    <button
      className={!disabled ? "orbit-menu-hover-button" : undefined}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: 12,
        height: subtitle ? 56 : 36,
        margin: 0,
        backgroundColor: "var(--bgPrimary)",
        border: "none",
        display: "flex",
        flexDirection: subtitle ? "column" : "row",
        lineHeight: 0,
        cursor: disabled ? "not-allowed" : "pointer",
        alignItems: shortcutKey ? "center" : undefined,
        width: shortcutKey ? "100%" : undefined,
        justifyContent: shortcutKey ? "space-between" : undefined,
      }}
    >
      <Label
        text={title}
        color={disabled ? LabelColor.FGDisabled : LabelColor.AccentPrimary}
      />
      {subtitle && (
        <>
          <div style={{ height: 8 }} />
          <LabelSmall
            text={subtitle}
            color={disabled ? LabelColor.FGDisabled : LabelColor.FGSecondaryLarge}
          />
        </>
      )}
      {shortcutKey && <ShortcutKey shortcutKey={shortcutKey} hover={isHovered} />}
    </button>
  );
}

// ============================================================================
// OrbitMenuPromptVisibilityControl (from OrbitMenuPromptVisibilityControl.tsx)
// ============================================================================

interface SegmentProps {
  title: string;
  isActive: boolean;
  onClick: () => void;
}

function Segment({ title, isActive, onClick }: SegmentProps) {
  return (
    <button
      onClick={onClick}
      className={!isActive ? "orbit-menu-hover-button" : undefined}
      style={{
        border: "none",
        background: isActive ? "var(--bgPrimary)" : "none",
        paddingLeft: 12,
        paddingRight: 12,
        paddingTop: 4,
        paddingBottom: 6,
        margin: 0,
        flexGrow: 1,
        cursor: "pointer",
      }}
    >
      <LabelSmall
        text={title}
        color={isActive ? LabelColor.AccentPrimary : LabelColor.FGPrimary}
      />
    </button>
  );
}

interface PromptVisibilityControlProps {
  value: PromptVisibilitySetting;
  onChange: (newValue: PromptVisibilitySetting) => void;
}

function OrbitMenuPromptVisibilityControl({ value, onChange }: PromptVisibilityControlProps) {
  const separatorStyle: React.CSSProperties = {
    width: 2,
    backgroundColor: "var(--fgTertiary)",
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        padding: 3,
        backgroundColor: "var(--bgSecondary)",
      }}
    >
      <Segment
        title={PromptVisibilitySetting.None}
        isActive={value === PromptVisibilitySetting.None}
        onClick={() => onChange(PromptVisibilitySetting.None)}
      />
      <div
        style={{
          ...separatorStyle,
          opacity: 1,
        }}
      />
      <Segment
        title={PromptVisibilitySetting.All}
        isActive={value === PromptVisibilitySetting.All}
        onClick={() => onChange(PromptVisibilitySetting.All)}
      />
    </div>
  );
}

// ============================================================================
// PromptVisibilityMenuItem (from OrbitMenu.tsx lines 143-163)
// ============================================================================

interface PromptVisibilityMenuItemProps {
  value: PromptVisibilitySetting;
  onChange: (value: PromptVisibilitySetting) => void;
}

function PromptVisibilityMenuItem({ value, onChange }: PromptVisibilityMenuItemProps) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "12px 12px 8px 12px",
      }}
    >
      <Label text="Show floating prompts:" color={LabelColor.FGPrimary} />
      <div style={{ height: 8 }} />
      <OrbitMenuPromptVisibilityControl value={value} onChange={onChange} />
    </div>
  );
}

// ============================================================================
// ToggleSwitch Component
// ============================================================================

interface ToggleSwitchProps {
  isOn: boolean;
  onToggle: () => void;
}

function ToggleSwitch({ isOn, onToggle }: ToggleSwitchProps) {
  return (
    <button
      onClick={onToggle}
      style={{
        width: 36,
        height: 20,
        borderRadius: 10,
        border: "none",
        padding: 2,
        cursor: "pointer",
        backgroundColor: isOn ? "var(--accentPrimary)" : "var(--bgSecondary)",
        transition: "background-color 0.15s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: isOn ? "flex-end" : "flex-start",
      }}
    >
      <div
        style={{
          width: 16,
          height: 16,
          borderRadius: 8,
          backgroundColor: isOn ? "white" : "var(--fgSecondarySmall)",
          transition: "background-color 0.15s ease",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.15)",
        }}
      />
    </button>
  );
}

// ============================================================================
// ToggleMenuItem Component
// ============================================================================

interface ToggleMenuItemProps {
  title: string;
  isOn: boolean;
  onToggle: () => void;
}

function ToggleMenuItem({ title, isOn, onToggle }: ToggleMenuItemProps) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 12px",
        backgroundColor: "var(--bgPrimary)",
      }}
    >
      <Label text={title} color={LabelColor.FGPrimary} />
      <ToggleSwitch isOn={isOn} onToggle={onToggle} />
    </div>
  );
}

// ============================================================================
// Main OrbitMenu Component (from OrbitMenu.tsx lines 169-359)
// ============================================================================

export interface PdfOrbitMenuProps {
  onBackToPdfs: () => void;
  promptCount?: number;
  duePromptCount?: number;
  onStartReview?: () => void;
  onExportAnki?: () => void;
  promptVisibility?: PromptVisibilitySetting;
  onPromptVisibilityChange?: (value: PromptVisibilitySetting) => void;
  showTranslations?: boolean;
  onShowTranslationsChange?: (value: boolean) => void;
  showArabicText?: boolean;
  onShowArabicTextChange?: (value: boolean) => void;
}

export function PdfOrbitMenu({
  onBackToPdfs,
  promptCount = 0,
  duePromptCount = 0,
  onStartReview,
  onExportAnki,
  promptVisibility = PromptVisibilitySetting.All,
  onPromptVisibilityChange,
  showTranslations = true,
  onShowTranslationsChange,
  showArabicText = true,
  onShowArabicTextChange,
}: PdfOrbitMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const interiorRef = useRef<HTMLDivElement | null>(null);
  const [contentsSize, setContentsSize] = useState<[number, number] | null>(null);

  // Calculate menu dimensions (OrbitMenu.tsx lines 188-196)
  const calculateLayout = useCallback(() => {
    if (interiorRef.current) {
      const rect = interiorRef.current.getBoundingClientRect();
      setContentsSize([rect.width, rect.height]);
    } else {
      setContentsSize(null);
    }
  }, []);

  useEffect(() => {
    setTimeout(() => {
      calculateLayout();
    }, 100);
  }, [calculateLayout]);

  // Close on outside click (OrbitMenu.tsx lines 206-221)
  useEffect(() => {
    if (!isOpen) return;

    function onClick(event: MouseEvent) {
      if (
        event.target instanceof Element &&
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [isOpen]);

  const anyPromptsSaved = promptCount > 0;

  // Start Review menu item with badge (OrbitMenu.tsx lines 223-256)
  const startReviewMenuItem = (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <MenuItem
        title="Start Review (⇧J)"
        isEnabled={duePromptCount > 0}
        onClick={() => {
          onStartReview?.();
          setIsOpen(false);
        }}
        disabled={duePromptCount === 0}
      />
      {duePromptCount > 0 && (
        <div
          style={{
            position: "absolute",
            top: 8,
            right: 10,
          }}
        >
          <DueCount count={duePromptCount} menuIsOpen={isOpen} context="menuItem" />
        </div>
      )}
    </div>
  );

  // Menu items (OrbitMenu.tsx lines 258-287)
  const items: ReactNode = (
    <>
      {onPromptVisibilityChange && (
        <PromptVisibilityMenuItem
          value={promptVisibility}
          onChange={onPromptVisibilityChange}
        />
      )}
      {onShowTranslationsChange && (
        <ToggleMenuItem
          title="Show translations"
          isOn={showTranslations}
          onToggle={() => onShowTranslationsChange(!showTranslations)}
        />
      )}
      {onShowArabicTextChange && (
        <ToggleMenuItem
          title="Show Arabic text"
          isOn={showArabicText}
          onToggle={() => onShowArabicTextChange(!showArabicText)}
        />
      )}
      {onStartReview && startReviewMenuItem}
    </>
  );

  return (
    <>
      {/* Inject hover styles */}
      <style>{hoverActiveButtonStyle}</style>

      <div className="pdfOrbitMenuContainer" ref={containerRef}>
        {/* Background */}
        {contentsSize && (
          <OrbitMenuBackground menuIsOpen={isOpen} contentsSize={contentsSize} />
        )}

        {/* Interior content */}
        <div
          ref={useCallback(
            (node: HTMLDivElement | null) => {
              interiorRef.current = node;
              calculateLayout();
            },
            [calculateLayout]
          )}
          style={{
            display: "flex",
            flexDirection: "column",
            // Only show background when menu is open; when closed, let OrbitMenuBackground show through
            backgroundColor: isOpen ? "var(--bgPrimary)" : "transparent",
            padding: 4,
            clipPath:
              isOpen || !contentsSize
                ? "inset(0 0 0 0 round 0 0 24px 0)"
                : `inset(${contentsSize[1] - 48}px 0 0 ${contentsSize[0] - 48}px round 24px)`,
            pointerEvents: isOpen ? "all" : "none",
            opacity: contentsSize ? 1 : 0,
            transition: "clip-path 0.3s var(--expoTiming), opacity 0s 0.3s linear",
          }}
        >
          {items}

          {/* Bottom bar with logo (OrbitMenu.tsx lines 322-339) */}
          <div
            style={{
              backgroundColor: "var(--bgSecondary)",
              borderTopWidth: 3,
              borderTopColor: "var(--fgTertiary)",
              borderTopStyle: "solid",
              height: 48,
              margin: -4,
              marginTop: 4,
              opacity: isOpen ? 1 : 0,
              transition: "var(--fadeTransition)",
              padding: 8,
              paddingTop: 8 - 3,
            }}
          >
            <OrbitMenuLogo />
          </div>
        </div>

        {/* Toggle button */}
        <OrbitMenuButton onClick={() => setIsOpen((o) => !o)} menuIsOpen={isOpen} />

        {/* DueCount badge on starburst (OrbitMenu.tsx lines 342-356) */}
        {promptCount > 0 && (
          <div
            style={{
              position: "absolute",
              bottom: 30,
              right: -10,
            }}
          >
            <DueCount count={promptCount} menuIsOpen={isOpen} context="orbitButton" />
          </div>
        )}
      </div>
    </>
  );
}

export default PdfOrbitMenu;
